/**
 * Reine Logik fuer den Erhalt der Wizard-Auswahl beim Schrittwechsel
 * (Phase wizard-auswahl-erhalten).
 *
 * KEIN React, KEIN API-Zugriff, KEINE Seiteneffekte — nur Mengenabgleich. Konsumiert von
 * App.tsx (loadArticles), analog zum Muster von categoryFilter.ts / categorySuggest.ts.
 *
 * HINTERGRUND: Schritt 2 -> 3 hat die getroffene Variantenauswahl bisher pauschal verworfen
 * (`selectedVariants: new Map()`) und danach in loadArticles ALLES neu vorselektiert. Wer aus
 * Schritt 3 oder 4 zurueck nach Schritt 2 ging und wieder vorwaerts, verlor damit jede
 * abgewaehlte Farbe. Der Artikel-Reload selbst ist richtig und bleibt — der Pool (flagOne=29)
 * kann sich zwischendurch geaendert haben —, nur das Verwerfen der Auswahl war falsch.
 */

export interface SelectableItem {
  itemId: number;
  mainVariation: { id: number };
  childVariations: Array<{ id: number }>;
}

/**
 * Waehlbare Varianten-IDs eines Artikels: die Kindvarianten, und nur wenn es keine gibt die
 * Hauptvariante. Einzige Quelle dieser Regel — der frueher parallele Helfer
 * createInitialVariantsMap in App.tsx ist damit entfallen.
 */
export function selectableVariantIds(item: SelectableItem): number[] {
  return item.childVariations.length > 0
    ? item.childVariations.map(cv => cv.id)
    : [item.mainVariation.id];
}

/**
 * Fuehrt die frisch geladene Artikelliste mit der bereits getroffenen Auswahl zusammen.
 *
 * Regeln je Artikel:
 * - noch nie gesehen (kein Eintrag in `previous`) -> alle Varianten vorselektiert (bisheriges
 *   Verhalten beim Erstladen bleibt damit unveraendert)
 * - bekannt -> vorherige Auswahl, geschnitten mit den noch existierenden Varianten. Varianten,
 *   die es in Plenty nicht mehr gibt, fallen still weg.
 * - bekannt und BEWUSST leer (User hat alle Varianten abgewaehlt) -> bleibt leer. Genau das
 *   war vorher nicht moeglich, weil die Auswahl neu vorselektiert wurde.
 * - bekannt, vorher nicht leer, aber keine der Varianten existiert noch -> wie neu behandelt
 *   (alles vorselektiert). Sonst haette ein kompletter Variantentausch in Plenty einen
 *   scheinbar abgewaehlten Artikel hinterlassen.
 *
 * Artikel, die nicht mehr in `items` stehen (aus dem Pool entfernt), verschwinden aus der Map.
 */
export function mergeVariantSelection(
  items: SelectableItem[],
  previous: Map<number, Set<number>>,
): Map<number, Set<number>> {
  const merged = new Map<number, Set<number>>();

  for (const item of items) {
    const selectable = selectableVariantIds(item);
    const vorher = previous.get(item.itemId);

    if (!vorher) {
      merged.set(item.itemId, new Set(selectable));
      continue;
    }

    const behalten = selectable.filter(id => vorher.has(id));
    if (behalten.length === 0 && vorher.size > 0) {
      merged.set(item.itemId, new Set(selectable)); // Variantentausch -> wie neu
    } else {
      merged.set(item.itemId, new Set(behalten));   // leer bleibt leer (bewusste Abwahl)
    }
  }

  return merged;
}

/**
 * Beschraenkt eine itemId-Map auf die uebergebenen Artikel — fuer druckProperties, damit keine
 * Eintraege von Artikeln zurueckbleiben, die nicht mehr im Pool sind. Rein defensiv: der
 * Batch-Aufbau liest die Properties ohnehin nur je vorhandenem Artikel.
 */
export function pruneItemMap<V>(map: Map<number, V>, itemIds: number[]): Map<number, V> {
  const erlaubt = new Set(itemIds);
  const result = new Map<number, V>();
  for (const [itemId, value] of map) {
    if (erlaubt.has(itemId)) result.set(itemId, value);
  }
  return result;
}
